import React from 'react'
import { motion } from 'motion/react'

interface ClampedBodyProps {
    /** The body as rendered — plain text on a checkin card, diff spans on a proposal. */
    children: React.ReactNode
    /**
     * Re-measure whenever this changes. Pass the underlying text: `children` are
     * fresh nodes on every render, so they can't serve as the dependency.
     */
    measureKey?: unknown
    /** Opens the full-body modal. Offered only when the text really is clipped. */
    onPeek: () => void
    /** Classes for the clamped paragraph itself (colour, pre-wrap, strike-through). */
    className?: string
    /** Classes for the wrapper, so each surface keeps its own spacing. */
    wrapperClassName?: string
}

/**
 * A checkin body clipped to three lines, with a "Read more" control that appears
 * only when the text is genuinely cut off.
 *
 * The overflow test is a real measurement (`scrollHeight` past `clientHeight` on
 * the clamped element) rather than a character-count guess, so the control never
 * opens a modal that shows nothing the user couldn't already read. That means
 * layout has to have happened: in an environment that computes none — jsdom —
 * both heights read 0 and nothing is offered, which is why the tests stub the
 * geometry instead of shortening the clamp.
 */
const ClampedBody: React.FC<ClampedBodyProps> = ({
    children,
    measureKey,
    onPeek,
    className = '',
    wrapperClassName = '',
}) => {
    const bodyRef = React.useRef<HTMLParagraphElement>(null)
    const [clipped, setClipped] = React.useState(false)

    React.useLayoutEffect(() => {
        const el = bodyRef.current
        if (!el) return
        // Sub-pixel line heights can leave scrollHeight a rounded pixel above
        // clientHeight on text that isn't actually cut off, so ask for more than
        // that before offering the modal.
        const measure = () => setClipped(el.scrollHeight - el.clientHeight > 1)
        measure()
        // The clamp is width-dependent: a body that fits on three lines in a wide
        // chat is clipped in a narrow one. Re-measure when the box resizes, where
        // the environment provides it.
        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(measure)
        observer.observe(el)
        return () => observer.disconnect()
    }, [measureKey])

    return (
        <div className={wrapperClassName}>
            <p ref={bodyRef} className={`line-clamp-3 ${className}`}>
                {children}
            </p>
            {clipped && (
                <motion.button
                    type="button"
                    onClick={onPeek}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="block mt-1 text-sm text-amber-800 hover:text-amber-900 cursor-pointer"
                >
                    Read more
                </motion.button>
            )}
        </div>
    )
}

export default ClampedBody
